import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Stack,
  Snackbar,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  TextField
} from '@mui/material'
import { attachmentService } from '../../../../shared/services/AttachmentService'
import { TicketAttachments } from '../../components/ticket/TicketAttachments'
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  TrendingUp as EscalateIcon,
  ForwardToInbox as ForwardIcon,
  Groups as GroupsIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  DeleteForever as DeleteIcon,
  MergeType as MergeIcon
} from '@mui/icons-material'
import { ticketService, WatcherInfo } from '../../../../shared/services/TicketService'
import { blockedSendersService } from '../../../../shared/services/BlockedSendersService'
import { userService } from '../../../../shared/services/UserService'
import { categoryService } from '../../../../shared/services/CategoryService'
import { EscalationService } from '../../../../shared/services/EscalationService'
import { OrganizationService } from '../../../../shared/services/OrganizationService'
import { Ticket, TicketStatus, TicketPriority, UserSummary, CategorySummary, TicketEscalation, Organization } from '../../../../shared/types/entities'
import { useAuth } from '../../../../shared/auth/AuthContext'
import { EscalationDialog } from '../../../../shared/components/EscalationDialog'
import { ForwardTicketDialog } from '../../components/ticket/ForwardTicketDialog'
import { MergeTicketDialog } from '../../components/ticket/MergeTicketDialog'
import { formatDateTime } from '@shared/utils/datetime'
import { useNotification, useTicketSubscription } from '../../hooks/useSignalR'

interface AgentTicketDetailProps {
  ticketId?: string // For modal mode
  isModal?: boolean // Whether displayed in modal or standalone
  onClose?: () => void // Close modal callback (handled by modal component)
  onUpdate?: () => void // Callback when ticket is updated (for refreshing list)
}

function AgentTicketDetail({ ticketId: propTicketId, isModal = false, onUpdate }: AgentTicketDetailProps = {}) {
  const { id: urlId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  // Use prop ticketId if in modal mode, otherwise use URL param
  const id = propTicketId || urlId
  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [agents, setAgents] = useState<UserSummary[]>([])
  const [categories, setCategories] = useState<CategorySummary[]>([])
  const [sites, setSites] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Local state for editing
  const [status, setStatus] = useState<TicketStatus>('Open')
  const [priority, setPriority] = useState<TicketPriority>('Medium')
  const [assignedAgentId, setAssignedAgentId] = useState<string>('')
  const [categoryId, setCategoryId] = useState<string>('')
  const [childSiteId, setChildSiteId] = useState<string>('')

  // Watch state
  const [isWatching, setIsWatching] = useState(false)
  const [watchers, setWatchers] = useState<WatcherInfo[]>([])
  const [watchLoading, setWatchLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'error' })

  // Escalation state
  const [escalations, setEscalations] = useState<TicketEscalation[]>([])
  const [escalationDialogOpen, setEscalationDialogOpen] = useState(false)

  // Forward dialog state
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false)

  // Merge dialog state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false)

  // Block sender dialog state
  const [blockSenderDialogOpen, setBlockSenderDialogOpen] = useState(false)
  const [blockDomain, setBlockDomain] = useState(false)
  const [blockLoading, setBlockLoading] = useState(false)

  // Delete ticket dialog state (Admin only)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    loadTicket()
    loadAgents()
    loadCategories()
    loadSites()
    if (id) {
      loadEscalations(id)
    }
  }, [id])

  const loadTicket = async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const data = await ticketService.getTicket(id)
      setTicket(data)
      setStatus(data.status)
      setPriority(data.priority)
      setAssignedAgentId(data.assignedAgentId || '')
      setCategoryId(data.categoryId || '')
      setChildSiteId(data.childSiteId || '')

      // Load watch status and watchers if this is a group ticket
      if (data.isGroupTicket) {
        loadWatchStatusForTicket(data.ticketId)
        loadWatchersForTicket(data.ticketId)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load ticket')
    } finally {
      setLoading(false)
    }
  }

  const loadAgents = async () => {
    try {
      const response = await userService.getUsers(1, 100)
      const agentUsers = response.data.filter(u => u.role === 'Agent' || u.role === 'Admin')
      setAgents(agentUsers)
    } catch (err: any) {
      console.error('Failed to load agents:', err)
    }
  }

  const loadCategories = async () => {
    try {
      const data = await categoryService.getCategories()
      setCategories(data)
    } catch (err: any) {
      console.error('Failed to load categories:', err)
    }
  }

  const loadSites = async () => {
    try {
      const data = await OrganizationService.getOrganizations()
      setSites(data)
    } catch (err: any) {
      console.error('Failed to load sites:', err)
    }
  }

  const loadWatchStatusForTicket = async (ticketId: string) => {
    try {
      const watching = await ticketService.isWatchingTicket(ticketId)
      setIsWatching(watching)
    } catch (error) {
      console.error('Failed to load watch status:', error)
    }
  }

  const loadWatchersForTicket = async (ticketId: string) => {
    try {
      const watchersList = await ticketService.getWatchers(ticketId)
      setWatchers(watchersList)
    } catch (error) {
      console.error('Failed to load watchers:', error)
    }
  }

  const loadEscalations = async (ticketId: string) => {
    try {
      const escalationsList = await EscalationService.getTicketEscalations(ticketId)
      setEscalations(escalationsList)
    } catch (error) {
      console.error('Failed to load escalations:', error)
    }
  }

  const handleEscalationSuccess = () => {
    setSnackbar({ open: true, message: 'Ticket escalated successfully', severity: 'success' })
    if (id) {
      loadEscalations(id)
      loadTicket() // Reload ticket to update assigned agent
    }
  }

  const handleBlockSender = async () => {
    if (!id) return
    try {
      setBlockLoading(true)
      await blockedSendersService.blockSenderFromTicket(id, blockDomain)
      setBlockSenderDialogOpen(false)
      setBlockDomain(false)
      setSnackbar({ open: true, message: 'Sender blocked and ticket closed as spam.', severity: 'success' })
      await loadTicket()
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to block sender.', severity: 'error' })
    } finally {
      setBlockLoading(false)
    }
  }

  const handleDeleteTicket = async () => {
    if (!id) return
    try {
      setDeleteLoading(true)
      await ticketService.deleteTicket(id, deleteReason.trim())
      setDeleteDialogOpen(false)
      // Ticket is gone — leave the detail view and return to the list
      navigate(user?.role === 'Admin' ? '/admin/tickets' : '/agent/queue', {
        state: { flash: 'Ticket deleted.' }
      })
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to delete ticket.', severity: 'error' })
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleToggleWatch = async () => {
    if (!ticket?.ticketId) return
    setWatchLoading(true)
    try {
      if (isWatching) {
        await ticketService.unwatchTicket(ticket.ticketId)
        setIsWatching(false)
        setSnackbar({ open: true, message: 'You are no longer watching this ticket', severity: 'info' })
      } else {
        await ticketService.watchTicket(ticket.ticketId)
        setIsWatching(true)
        setSnackbar({ open: true, message: 'You are now watching this ticket', severity: 'success' })
      }
      await loadWatchersForTicket(ticket.ticketId)
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to update watch status', severity: 'error' })
    } finally {
      setWatchLoading(false)
    }
  }

  const handleToggleGroupTicket = async () => {
    if (!ticket?.ticketId) return
    setLoading(true)
    try {
      const newGroupState = !ticket.isGroupTicket
      await ticketService.updateTicket(ticket.ticketId, { isGroupTicket: newGroupState })
      setTicket({ ...ticket, isGroupTicket: newGroupState })

      // Load/reload watchers if becoming a group ticket
      if (newGroupState) {
        loadWatchStatusForTicket(ticket.ticketId)
        loadWatchersForTicket(ticket.ticketId)
      }

      setSnackbar({
        open: true,
        message: newGroupState
          ? 'Ticket is now a group ticket - all organization users can view and collaborate'
          : 'Ticket is no longer a group ticket',
        severity: 'success'
      })
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to update group ticket status', severity: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleSaveChanges = async () => {
    if (!ticket || !id) return

    // Validate category is required
    if (!categoryId) {
      setError('Category is required. Please select a category before saving.')
      return
    }

    try {
      setSaving(true)
      setError(null)
      setSuccessMessage(null)

      // The dropdown mixes organisations (no site) and sites; send the matching field for each
      const selectedSite = sites.find(s => (s.childSiteId || s.organizationId) === childSiteId)
      const isSite = !!selectedSite?.childSiteId

      // Update ticket with all values including categoryId and site/organisation
      await ticketService.updateTicket(id, {
        status,
        priority,
        subject: ticket.subject,
        description: ticket.description,
        assignedAgentId: assignedAgentId || undefined,
        categoryId: categoryId,
        childSiteId: isSite ? childSiteId : undefined,
        organisationId: childSiteId && !isSite ? childSiteId : undefined,
        tags: ticket.tags,
        severity: ticket.severity,
        dueDate: ticket.dueDate,
        resolutionNotes: ticket.resolutionNotes
      })

      setSuccessMessage('Ticket updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)

      // Reload ticket to get updated data
      await loadTicket()

      // Notify parent component to refresh if in modal mode
      if (onUpdate) {
        onUpdate()
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update ticket')
    } finally {
      setSaving(false)
    }
  }

  const hasChanges = () => {
    if (!ticket) return false
    return (
      status !== ticket.status ||
      priority !== ticket.priority ||
      categoryId !== (ticket.categoryId || '') ||
      childSiteId !== (ticket.childSiteId || '') ||
      (assignedAgentId && assignedAgentId !== ticket.assignedAgentId)
    )
  }

  const getStatusColor = (status: string): 'default' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'Open':
        return 'warning'
      case 'InProgress':
        return 'info'
      case 'OnHold':
        return 'warning'
      case 'WaitingOnThirdParty':
        return 'warning'
      case 'Resolved':
        return 'success'
      case 'Closed':
        return 'default'
      default:
        return 'default'
    }
  }

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'Urgent':
        return '#d32f2f'
      case 'High':
        return '#f57c00'
      case 'Medium':
        return '#fbc02d'
      case 'Low':
        return '#388e3c'
      default:
        return '#757575'
    }
  }

  const formatDate = (dateString: string): string =>
    formatDateTime(dateString, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  if (error && !ticket) {
    return (
      <Box>
        {!isModal && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(user?.role === 'Admin' ? '/admin/tickets' : '/agent/queue')}
            sx={{ mb: 3 }}
          >
            {user?.role === 'Admin' ? 'Back to Tickets' : 'Back to Queue'}
          </Button>
        )}
        <Alert severity="error">
          {error || 'Ticket not found'}
        </Alert>
      </Box>
    )
  }

  if (!ticket) {
    return (
      <Box>
        {!isModal && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(user?.role === 'Admin' ? '/admin/tickets' : '/agent/queue')}
            sx={{ mb: 3 }}
          >
            {user?.role === 'Admin' ? 'Back to Tickets' : 'Back to Queue'}
          </Button>
        )}
        <Alert severity="error">Ticket not found</Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        {!isModal && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(user?.role === 'Admin' ? '/admin/tickets' : '/agent/queue')}
          >
            {user?.role === 'Admin' ? 'Back to Tickets' : 'Back to Queue'}
          </Button>
        )}
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveChanges}
            disabled={saving || !hasChanges()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          {ticket?.isGroupTicket && (
            <Button
              variant={isWatching ? "contained" : "outlined"}
              startIcon={isWatching ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={handleToggleWatch}
              disabled={watchLoading || saving}
            >
              {isWatching ? 'Watching' : 'Watch Ticket'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ForwardIcon />}
            onClick={() => setForwardDialogOpen(true)}
            disabled={saving}
          >
            Forward
          </Button>
          <Button
            variant="outlined"
            startIcon={<EscalateIcon />}
            onClick={() => setEscalationDialogOpen(true)}
            disabled={saving}
          >
            Escalate
          </Button>
          <Button
            variant="outlined"
            startIcon={<MergeIcon />}
            onClick={() => setMergeDialogOpen(true)}
            disabled={saving}
          >
            Merge
          </Button>
          {ticket?.source === 'Email' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<BlockIcon />}
              onClick={() => { setBlockSenderDialogOpen(true); setBlockDomain(false) }}
              disabled={saving}
            >
              Block Sender
            </Button>
          )}
          {user?.role === 'Admin' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => { setDeleteReason(''); setDeleteDialogOpen(true) }}
              disabled={saving}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Ticket Details Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
            <Box>
              <Typography variant="caption" color="textSecondary" display="block">
                {ticket.ticketNumber}
              </Typography>
              <Typography variant="h4" gutterBottom>
                {ticket.subject}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Chip
                label={ticket.status}
                color={getStatusColor(ticket.status)}
              />
              <Chip
                label={ticket.priority}
                sx={{
                  bgcolor: getPriorityColor(ticket.priority),
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Ticket Visibility */}
          <Box sx={{ mb: 3, p: 2.5, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {ticket.isGroupTicket ? <GroupsIcon color="primary" /> : <PersonIcon color="action" />}
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {ticket.isGroupTicket ? 'Group Ticket' : 'Personal Ticket'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {ticket.isGroupTicket
                    ? 'All organization users can view and collaborate'
                    : 'Only visible to requester, assigned agent, and watchers'}
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={ticket.isGroupTicket || false}
              onChange={handleToggleGroupTicket}
              disabled={loading}
              color="primary"
            />
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary" display="block">
                Created
              </Typography>
              <Typography variant="body1">{formatDate(ticket.createdAt)}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary" display="block">
                Last Updated
              </Typography>
              <Typography variant="body1">{formatDate(ticket.updatedAt)}</Typography>
            </Grid>
            {ticket.resolvedAt && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Resolved
                </Typography>
                <Typography variant="body1" color="success.main">{formatDate(ticket.resolvedAt)}</Typography>
              </Grid>
            )}
            {ticket.closedAt && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Closed
                </Typography>
                <Typography variant="body1" color="text.secondary">{formatDate(ticket.closedAt)}</Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary" display="block">
                Requester
              </Typography>
              <Typography variant="body1">{ticket.requesterName || 'Unknown'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary" display="block">
                Source
              </Typography>
              <Typography variant="body1">{ticket.source}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary" display="block">
                Category
              </Typography>
              <Typography variant="body1">
                {ticket.categoryName || <em style={{ color: '#999' }}>Not assigned</em>}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary" display="block">
                Assigned Site
              </Typography>
              <Typography variant="body1">
                {ticket.childSiteName || <em style={{ color: '#999' }}>Tenant Level</em>}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
            Description
          </Typography>
          <Paper elevation={0} sx={{ bgcolor: 'grey.50', p: 2, mb: 3 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {ticket.description}
            </Typography>
          </Paper>
        </CardContent>
      </Card>

      {/* Watchers Card */}
      {ticket?.isGroupTicket && watchers && watchers.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Watchers ({watchers.length})
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1}>
              {watchers.map((watcher) => (
                <Box key={watcher.watcherId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={watcher.userName}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {watcher.userEmail}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Escalation History Card */}
      {escalations && escalations.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Escalation History ({escalations.length})
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              {escalations.map((escalation) => (
                <Paper key={escalation.escalationId} elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {escalation.escalatedToUserName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Escalated by {escalation.escalatedByUserName}
                      </Typography>
                    </Box>
                    <Chip
                      label={escalation.escalationType}
                      size="small"
                      color={escalation.escalationType === 'AutoSLA' ? 'warning' : 'default'}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Reason:</strong> {escalation.escalationReason}
                  </Typography>
                  {escalation.escalationNotes && (
                    <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic' }}>
                      {escalation.escalationNotes}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(escalation.escalatedAt)}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Agent Action Controls Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Ticket Actions
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value as TicketStatus)}
                >
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="InProgress">In Progress</MenuItem>
                  <MenuItem value="OnHold">On Hold</MenuItem>
                  <MenuItem value="WaitingOnThirdParty">Waiting on Third Party</MenuItem>
                  <MenuItem value="Resolved">Resolved</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priority}
                  label="Priority"
                  onChange={(e) => setPriority(e.target.value as TicketPriority)}
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Assigned Agent</InputLabel>
                <Select
                  value={assignedAgentId}
                  label="Assigned Agent"
                  onChange={(e) => setAssignedAgentId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {agents.map((agent) => (
                    <MenuItem key={agent.userId} value={agent.userId}>
                      {agent.fullName} ({agent.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth required error={!categoryId}>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={categoryId}
                  label="Category *"
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select a category</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.categoryId} value={category.categoryId}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Assign to Site</InputLabel>
                <Select
                  value={childSiteId}
                  label="Assign to Site"
                  onChange={(e) => setChildSiteId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Tenant Level (No Site)</em>
                  </MenuItem>
                  {sites.map((site) => (
                    <MenuItem key={site.childSiteId || site.organizationId} value={site.childSiteId || site.organizationId}>
                      {site.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {hasChanges() && (
            <Alert severity="info" sx={{ mt: 3 }}>
              You have unsaved changes. Click "Save Changes" to update the ticket.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Comments Section */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Comments
          </Typography>
          <CommentsSection ticketId={id!} />
        </CardContent>
      </Card>

      {/* Attachments Section */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Attachments
          </Typography>
          <TicketAttachments ticketId={id!} />
        </CardContent>
      </Card>

      {/* Escalation Dialog */}
      <EscalationDialog
        open={escalationDialogOpen}
        onClose={() => setEscalationDialogOpen(false)}
        ticketId={id!}
        agents={agents}
        onSuccess={handleEscalationSuccess}
      />

      {/* Forward Ticket Dialog */}
      {ticket && (
        <ForwardTicketDialog
          open={forwardDialogOpen}
          onClose={() => setForwardDialogOpen(false)}
          ticketId={ticket.ticketId}
          ticketNumber={ticket.ticketNumber}
          attachmentCount={0}
          onForwardSuccess={() => {
            setSnackbar({
              open: true,
              message: 'Ticket forwarded successfully!',
              severity: 'success'
            })
            loadTicket() // Reload to show system message in timeline
          }}
        />
      )}

      {/* Merge Ticket Dialog */}
      {ticket && (
        <MergeTicketDialog
          open={mergeDialogOpen}
          onClose={() => setMergeDialogOpen(false)}
          sourceTicketId={ticket.ticketId}
          sourceTicketNumber={ticket.ticketNumber}
          onMergeSuccess={(targetTicketId) => {
            setSnackbar({ open: true, message: 'Ticket merged successfully.', severity: 'success' })
            if (onUpdate) onUpdate() // refresh any underlying list
            if (isModal) {
              loadTicket() // reflect the now-merged/closed state + system note
            } else {
              navigate(`/agent/tickets/${targetTicketId}`) // go to the surviving ticket
            }
          }}
        />
      )}

      {/* Block Sender Dialog */}
      {ticket && (
        <Dialog open={blockSenderDialogOpen} onClose={() => setBlockSenderDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Block Sender</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This will close the ticket as spam and prevent future emails from this sender creating new tickets.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sender: <strong>{ticket.requesterName}</strong>
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={blockDomain}
                  onChange={(e) => setBlockDomain(e.target.checked)}
                />
              }
              label="Block entire domain (not just this address)"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBlockSenderDialogOpen(false)} disabled={blockLoading}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleBlockSender}
              disabled={blockLoading}
              startIcon={blockLoading ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
            >
              Block & Close Ticket
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Ticket Dialog (Admin only) */}
      {ticket && (
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Ticket {ticket.ticketNumber}</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              The ticket will be removed from all views, queues, and statistics. This action is
              recorded in the audit log against your account.
            </Alert>
            <TextField
              label="Reason for deletion"
              placeholder="e.g. Spam / duplicate of TKT-2026-000012 / created in error"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              fullWidth
              required
              multiline
              rows={3}
              autoFocus
              helperText={`${deleteReason.trim().length}/1000 — a reason of at least 5 characters is required`}
              error={deleteReason.length > 0 && deleteReason.trim().length < 5}
              inputProps={{ maxLength: 1000 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteTicket}
              disabled={deleteLoading || deleteReason.trim().length < 5}
              startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            >
              Delete Ticket
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar for watch notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

// Comments Section Component
function CommentsSection({ ticketId }: { ticketId: string }) {
  const [messages, setMessages] = useState<any[]>([])
  const [newComment, setNewComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInternal, setIsInternal] = useState(false)

  // Subscribe to this ticket for real-time updates
  useTicketSubscription(ticketId)

  useEffect(() => {
    loadMessages()
  }, [ticketId])

  // Real-time notification: reload messages when a new message is posted
  useNotification('NewMessage', (data: any) => {
    // Only reload if the message is for this ticket
    if (data.ticketId === ticketId) {
      loadMessages()
    }
  }, [ticketId])

  const loadMessages = async () => {
    try {
      setLoading(true)
      setError(null)
      const { ticketMessageService } = await import('../../../../shared/services/TicketMessageService')
      const data = await ticketMessageService.getTicketMessages(ticketId)
      setMessages(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load comments')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newComment.trim()) return

    try {
      setSubmitting(true)
      setError(null)
      const { ticketMessageService } = await import('../../../../shared/services/TicketMessageService')
      await ticketMessageService.createMessage({
        ticketId,
        content: newComment,
        isInternal: isInternal
      })
      setNewComment('')
      setIsInternal(false)
      await loadMessages()
    } catch (err: any) {
      setError(err.message || 'Failed to post comment')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error && messages.length === 0) {
    return <Alert severity="error">{error}</Alert>
  }

  return (
    <Box>
      {messages.length === 0 ? (
        <Typography color="textSecondary" sx={{ py: 2 }}>
          No comments yet. Be the first to comment!
        </Typography>
      ) : (
        <Box sx={{ mb: 3 }}>
          {messages.map((message) => (
            <Box
              key={message.messageId}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {message.authorName}
                    <Chip
                      label={message.authorRole}
                      size="small"
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      color={message.authorRole === 'Agent' || message.authorRole === 'Admin' ? 'primary' : 'default'}
                    />
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatDateTime(message.createdAt)}
                  </Typography>
                </Box>
                {message.isInternal && (
                  <Chip label="Internal Note" size="small" color="warning" />
                )}
              </Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                {message.content}
              </Typography>
              {message.attachments && message.attachments.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                    Attachments:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {message.attachments.map((att: any) => (
                      <Chip
                        key={att.attachmentId}
                        label={`${att.originalFileName} (${att.fileSizeFormatted})`}
                        size="small"
                        onClick={() => window.open(attachmentService.getDownloadUrl(att.attachmentId), '_blank')}
                        sx={{ maxWidth: '100%' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 2 }}>
          <Box
            component="textarea"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment or reply to the customer..."
            disabled={submitting}
            sx={{
              width: '100%',
              minHeight: '100px',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              resize: 'vertical',
              '&:focus': {
                outline: 'none',
                borderColor: 'primary.main'
              }
            }}
          />
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <FormControl>
              <Box display="flex" alignItems="center">
                <input
                  type="checkbox"
                  id="internal-note"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <InputLabel htmlFor="internal-note" sx={{ position: 'static', transform: 'none', fontSize: '0.875rem', cursor: 'pointer' }}>
                  Internal note (not visible to customer)
                </InputLabel>
              </Box>
            </FormControl>
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={!newComment.trim() || submitting}
          >
            {submitting ? 'Posting...' : 'Post Comment'}
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {!isInternal && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This comment will be visible to the customer and they will receive an email notification.
          </Alert>
        )}
      </form>
    </Box>
  )
}

export default AgentTicketDetail
